import type { PairedRuuviTag } from '../types'

export function computeRotationDegFromTag(tag: PairedRuuviTag): {
  x: number
  y: number
  z: number
} {
  const accel = tag.latest?.acceleration
  const rawX = accel?.xAvg ?? accel?.xMax ?? 0
  const rawY = accel?.yAvg ?? accel?.yMax ?? 0
  const rawZ = accel?.zAvg ?? accel?.zMax ?? 0

  const toNumber = (value: unknown): number => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0
    }

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const x = toNumber(rawX)
  const y = toNumber(rawY)
  const z = toNumber(rawZ)

  const magnitudeSq = x * x + y * y + z * z
  if (!Number.isFinite(magnitudeSq) || magnitudeSq < 1e-4) {
    return { x: -90, y: 0, z: 0 }
  }

  const magnitude = Math.sqrt(magnitudeSq)
  const xyMagnitude = Math.sqrt(x * x + y * y)

  const wrapDeg = (deg: number) => ((((deg + 180) % 360) + 360) % 360) - 180
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

  const tiltRatio = clamp(z / (magnitude || 1), -1, 1)
  let rotX = -((Math.asin(tiltRatio) * 180) / Math.PI)
  if (Math.abs(tiltRatio) > 0.98) {
    rotX = tiltRatio >= 0 ? -90 : 90
  } else if (Math.abs(z) < 120) {
    rotX = 0
  }

  const rotY = 0

  const yawOffsetDeg = 26
  let rotZ = wrapDeg((-Math.atan2(y, x) * 180) / Math.PI - yawOffsetDeg)

  if (xyMagnitude < 120 && Math.abs(z) > 400) {
    rotZ = z >= 0 ? -180 : 0
  }

  if (Math.abs(Math.abs(rotZ) - 180) <= 1.5) {
    rotZ = rotZ < 0 ? -180 : 180
  }

  return {
    x: wrapDeg(rotX),
    y: wrapDeg(rotY),
    z: wrapDeg(rotZ)
  }
}
